/**
 * FAST Studio Constants
 *
 * Defines workspaces, roles, and configuration for the unified hub.
 */

import HomeIcon from '@mui/icons-material/Home';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

// Workspace identifiers
export const WORKSPACE_IDS = {
  HOME: 'home',
  LLM_LAB: 'llm-lab',
  FLEX: 'flex',
  API_HOUND: 'api-hound',
  WORKFLOW_BUILDER: 'workflow-builder',
  INTEGRATION: 'integration',
  TASKS: 'tasks',
  WORKSPACES_MGMT: 'workspaces-mgmt',
};

// Role definitions (must match backend lookup_values)
export const ROLES = {
  OPERATOR: 'OPERATOR',
  SUPERVISOR: 'SUPERVISOR',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
  FOYSONIS_DEV: 'FOYSONIS_DEV',
};

// Workspace configuration
export const WORKSPACES = {
  [WORKSPACE_IDS.HOME]: {
    id: WORKSPACE_IDS.HOME,
    label: 'Home',
    shortLabel: 'Home',
    icon: HomeIcon,
    description: 'Welcome dashboard with quick actions and recent items',
    supportsTabs: false,
    allowedRoles: null, // All roles can access
    aiContext: 'general',
  },
  [WORKSPACE_IDS.LLM_LAB]: {
    id: WORKSPACE_IDS.LLM_LAB,
    label: 'Prompt Engineering',
    shortLabel: 'Prompts',
    icon: PsychologyIcon,
    description: 'Design, test, and manage agent prompts and system instructions',
    supportsTabs: true,
    // TODO: Re-enable role restrictions once user role assignment is implemented
    // allowedRoles: [ROLES.SUPERVISOR, ROLES.MANAGER, ROLES.ADMIN, ROLES.FOYSONIS_DEV],
    allowedRoles: null, // Temporarily allow all users
    aiContext: 'llm',
  },
  [WORKSPACE_IDS.FLEX]: {
    id: WORKSPACE_IDS.FLEX,
    label: 'Flex',
    shortLabel: 'Flex',
    icon: CodeIcon,
    description: 'FLEX DSL scripting environment for supply chain applications',
    supportsTabs: true,
    // TODO: Re-enable role restrictions once user role assignment is implemented
    // allowedRoles: [ROLES.MANAGER, ROLES.ADMIN, ROLES.FOYSONIS_DEV],
    allowedRoles: null, // Temporarily allow all users
    aiContext: 'flex',
  },
  [WORKSPACE_IDS.API_HOUND]: {
    id: WORKSPACE_IDS.API_HOUND,
    label: 'API & Testing',
    shortLabel: 'API',
    icon: ApiIcon,
    description: 'Test APIs from the registry with full request/response control',
    supportsTabs: true,
    // TODO: Re-enable role restrictions once user role assignment is implemented
    // allowedRoles: [ROLES.SUPERVISOR, ROLES.MANAGER, ROLES.ADMIN, ROLES.FOYSONIS_DEV],
    allowedRoles: null, // Temporarily allow all users
    aiContext: 'api',
  },
  [WORKSPACE_IDS.WORKFLOW_BUILDER]: {
    id: WORKSPACE_IDS.WORKFLOW_BUILDER,
    label: 'Workflow Builder',
    shortLabel: 'Wflow',
    icon: AccountTreeIcon,
    description: 'Design and manage guided workflows with drag-drop step editor',
    supportsTabs: false,
    allowedRoles: null, // Temporarily allow all users
    aiContext: 'workflow',
  },
  [WORKSPACE_IDS.INTEGRATION]: {
    id: WORKSPACE_IDS.INTEGRATION,
    label: 'Integration',
    shortLabel: 'Integ',
    icon: SyncAltIcon,
    description: 'Manage channels, message types, flows, and test inbound/outbound messages',
    supportsTabs: false,
    allowedRoles: null, // Temporarily allow all users
    aiContext: 'integration',
  },
  [WORKSPACE_IDS.TASKS]: {
    id: WORKSPACE_IDS.TASKS,
    label: 'Tasks',
    shortLabel: 'Tasks',
    icon: AssignmentIcon,
    description: 'Monitor background jobs and task execution',
    supportsTabs: false,
    allowedRoles: null, // All roles can access
    aiContext: 'tasks',
  },
  [WORKSPACE_IDS.WORKSPACES_MGMT]: {
    id: WORKSPACE_IDS.WORKSPACES_MGMT,
    label: 'Workspaces',
    shortLabel: 'Wksp',
    icon: ViewModuleIcon,
    description: 'Manage workspace definitions, ordering, and configuration',
    supportsTabs: false,
    allowedRoles: [ROLES.ADMIN, ROLES.FOYSONIS_DEV],
    aiContext: 'general',
  },
};

// Sidebar navigation order
export const SIDEBAR_ORDER = [
  WORKSPACE_IDS.HOME,
  WORKSPACE_IDS.LLM_LAB,
  WORKSPACE_IDS.FLEX,
  WORKSPACE_IDS.API_HOUND,
  WORKSPACE_IDS.WORKFLOW_BUILDER,
  WORKSPACE_IDS.INTEGRATION,
  WORKSPACE_IDS.TASKS,
  WORKSPACE_IDS.WORKSPACES_MGMT,
];

// AI Assistant contexts
export const AI_CONTEXTS = {
  general: {
    name: 'General Assistant',
    systemPromptKey: 'FAST_GENERAL_ASSISTANT',
    capabilities: ['answer questions', 'provide guidance', 'explain concepts'],
  },
  llm: {
    name: 'Prompt Engineer',
    systemPromptKey: 'FAST_PROMPT_ENGINEER',
    capabilities: ['write prompts', 'optimize prompts', 'explain variables', 'suggest improvements'],
  },
  flex: {
    name: 'FLEX Command Assistant',
    systemPromptKey: 'FLEX_SCRIPT_GENERATOR',
    capabilities: ['generate scripts', 'debug code', 'explain syntax', 'suggest entities'],
  },
  api: {
    name: 'API Helper',
    systemPromptKey: 'FAST_API_HELPER',
    capabilities: ['suggest headers', 'parse responses', 'debug requests', 'explain status codes'],
  },
  workflow: {
    name: 'Workflow Designer',
    systemPromptKey: 'FAST_WORKFLOW_DESIGNER',
    capabilities: ['design workflows', 'configure steps', 'define fields', 'setup validations', 'test flows'],
  },
  tasks: {
    name: 'Task Monitor Helper',
    systemPromptKey: 'FAST_TASK_HELPER',
    capabilities: ['explain task status', 'troubleshoot failures', 'suggest optimizations'],
  },
  integration: {
    name: 'Integration Assistant',
    systemPromptKey: 'FAST_INTEGRATION_ASSISTANT',
    capabilities: ['manage channels', 'troubleshoot messages', 'configure mappings', 'monitor health'],
  },
};

// Default sidebar width
export const SIDEBAR_WIDTH = {
  expanded: 230,
  collapsed: 56,
};

// AI Assistant dock width
export const AI_DOCK_WIDTH = {
  default: 320,
  min: 280,
  max: 500,
};

// Status bar height
export const STATUS_BAR_HEIGHT = 24;
